/**
 * F6 Kwaliteit — bevinding-templates (single-source-of-truth).
 *
 * Bron: productblauwdruk monteursapp §kwaliteit:
 *   - Toolbox-talk registratie
 *   - Veiligheidschecklist
 *   - KAM-rapportage (Kwaliteit/Arbo/Milieu)
 *
 * Templates zijn constanten (geen DB-tabel) zodat code-paden geen extra
 * round-trip nodig hebben. `templateKey` op `KwaliteitsTaak` referencet één
 * van deze IDs; null = vrije taak zonder template.
 */

import type { KwaliteitsTaakPrioriteit } from './kwaliteit'

export interface KwaliteitTemplateDef {
  /** Stabiele machine-key — gebruikt als `templateKey` op KwaliteitsTaak. */
  key: string
  /** UI-label, NL-talig. */
  label: string
  /** Korte uitleg + context van de template (≤ 200 tekens). */
  beschrijving: string
  /** Default prioriteit bij aanmaken vanuit deze template. */
  defaultPrioriteit: KwaliteitsTaakPrioriteit
  /** Standaard-omschrijving (vooringevuld in form). */
  standaardOmschrijving: string
}

export const KWALITEIT_TEMPLATES: ReadonlyArray<KwaliteitTemplateDef> = [
  {
    key: 'TOOLBOX',
    label: 'Toolbox-talk',
    beschrijving:
      'Korte registratie van een toolbox-talk op locatie - wie, wanneer, welk onderwerp en wie aanwezig waren.',
    defaultPrioriteit: 'NORMAAL',
    standaardOmschrijving:
      'Toolbox-talk uitgevoerd. Onderwerp: <invullen>. Aanwezigen: <invullen>. Belangrijkste afspraken: <invullen>.',
  },
  {
    key: 'VEILIGHEID',
    label: 'Veiligheidschecklist',
    beschrijving:
      'Pre-werk veiligheidschecklist of melding van onveilige situatie / bijna-ongeval ter plaatse.',
    defaultPrioriteit: 'HOOG',
    standaardOmschrijving:
      'Veiligheidsbevinding. Locatie: <invullen>. Beschrijving: <invullen>. Direct genomen actie: <invullen>.',
  },
  {
    key: 'KAM',
    label: 'KAM-rapportage',
    beschrijving:
      'Kwaliteit/Arbo/Milieu-rapportage - bijvoorbeeld afval, lekkage, klantklacht of milieu-incident.',
    defaultPrioriteit: 'NORMAAL',
    standaardOmschrijving:
      'KAM-rapportage. Type: <kwaliteit/arbo/milieu>. Beschrijving: <invullen>. Vervolgactie: <invullen>.',
  },
  {
    key: 'STEEKPROEF',
    label: 'Steekproef-controle',
    beschrijving:
      'Auto-gegenereerde steekproef-controle op goedgekeurde werkbon - interne audit van uitgevoerd werk.',
    defaultPrioriteit: 'NORMAAL',
    standaardOmschrijving:
      'Steekproef-controle op werkbon. Controleer: uitvoering, materiaal, opruimen en klantbeoordeling.',
  },
] as const

export type KwaliteitTemplateKey = (typeof KWALITEIT_TEMPLATES)[number]['key']

/** Lookup helper — geeft de template-def voor een key, of null als onbekend. */
export function getKwaliteitTemplate(key: string | null | undefined): KwaliteitTemplateDef | null {
  if (!key) return null
  return KWALITEIT_TEMPLATES.find((t) => t.key === key) ?? null
}
